import { useEffect, useMemo, useState } from "react";
import { Button, Field, SelectField, Spinner, StatusBadge } from "@leylion/design-system";
import {
  createPlatformIdempotencyKey,
  executePlatformSubscriptionCommand,
  getPlatformPlanDetail,
  getPlatformSubscriptionAdministrationState,
  previewPlatformPlanChange
} from "./platform-client";
import type {
  PlatformPlanChangePreview,
  PlatformPlanDetail,
  PlatformPlanSummary,
  PlatformSubscriptionAdministrationState
} from "./platform-types";
import "./platform-admin.css";

type Language = "ar" | "en";
type ActionId =
  | "extend-trial"
  | "end-trial"
  | "activate"
  | "extend-period"
  | "mark-past-due"
  | "extend-grace"
  | "restore-active"
  | "suspend"
  | "reactivate"
  | "schedule-cancellation"
  | "undo-cancellation"
  | "cancel-now"
  | "change-plan";

type ActionDefinition = {
  id: ActionId;
  ar: string;
  en: string;
  level: 1 | 2 | 3;
  phrase?: string;
  reasonGroup: keyof typeof REASONS;
  defaultReason?: string;
  dateKind?: "trial" | "period" | "grace";
  needsPlan?: boolean;
};

const REASONS = {
  trial: [
    "TRIAL_EVALUATION_EXTENSION",
    "TRIAL_IMPLEMENTATION_EXTENSION",
    "TRIAL_SUPPORT_RECOVERY",
    "TRIAL_COMMERCIAL_EXCEPTION"
  ],
  endTrial: [
    "TRIAL_CUSTOMER_REQUEST",
    "TRIAL_EVALUATION_COMPLETED",
    "TRIAL_COMMERCIAL_TERMINATION",
    "TRIAL_ADMINISTRATIVE_TERMINATION"
  ],
  activation: [
    "MANUAL_COMMERCIAL_ACTIVATION",
    "MANUAL_RENEWAL",
    "SERVICE_RECOVERY",
    "COMMERCIAL_EXCEPTION"
  ],
  collection: [
    "COLLECTION_PENDING",
    "COMMERCIAL_GRACE_APPROVED",
    "ACCOUNT_REVIEW_PENDING"
  ],
  suspension: ["COMMERCIAL_HOLD", "NON_PAYMENT_HOLD", "ACCOUNT_ADMIN_HOLD"],
  cancellation: ["CUSTOMER_REQUEST", "NON_RENEWAL", "COMMERCIAL_TERMINATION", "DUPLICATE_TENANT"],
  plan: ["CUSTOMER_UPGRADE", "CUSTOMER_DOWNGRADE", "PLAN_VERSION_MIGRATION", "COMMERCIAL_PLAN_CHANGE", "SUPPORT_CORRECTION"]
} as const;

const REASON_LABELS: Record<string, [string, string]> = {
  TRIAL_EVALUATION_EXTENSION: ["تمديد فترة التقييم", "Extend trial evaluation"],
  TRIAL_IMPLEMENTATION_EXTENSION: ["تمديد مرحلة التنفيذ", "Extend implementation period"],
  TRIAL_SUPPORT_RECOVERY: ["تعويض تعطل أو دعم", "Support recovery"],
  TRIAL_COMMERCIAL_EXCEPTION: ["استثناء تجاري للتجربة", "Trial commercial exception"],
  TRIAL_CUSTOMER_REQUEST: ["طلب العميل", "Customer request"],
  TRIAL_EVALUATION_COMPLETED: ["اكتمال التقييم", "Evaluation completed"],
  TRIAL_COMMERCIAL_TERMINATION: ["إنهاء تجاري للتجربة", "Trial commercial termination"],
  TRIAL_ADMINISTRATIVE_TERMINATION: ["إنهاء إداري للتجربة", "Trial administrative termination"],
  MANUAL_COMMERCIAL_ACTIVATION: ["تفعيل تجاري يدوي", "Manual commercial activation"],
  MANUAL_RENEWAL: ["تجديد يدوي", "Manual renewal"],
  SERVICE_RECOVERY: ["استعادة الخدمة", "Service recovery"],
  COMMERCIAL_EXCEPTION: ["استثناء تجاري", "Commercial exception"],
  COLLECTION_PENDING: ["تحصيل مستحق قيد المتابعة", "Collection pending"],
  COMMERCIAL_GRACE_APPROVED: ["فترة سماح تجارية معتمدة", "Approved commercial grace"],
  ACCOUNT_REVIEW_PENDING: ["مراجعة الحساب قيد الانتظار", "Account review pending"],
  COMMERCIAL_HOLD: ["تعليق تجاري", "Commercial hold"],
  NON_PAYMENT_HOLD: ["تعليق بسبب عدم السداد", "Non-payment hold"],
  ACCOUNT_ADMIN_HOLD: ["تعليق إداري للحساب", "Account administrative hold"],
  CUSTOMER_REQUEST: ["طلب العميل", "Customer request"],
  NON_RENEWAL: ["عدم التجديد", "Non-renewal"],
  COMMERCIAL_TERMINATION: ["إنهاء تجاري", "Commercial termination"],
  DUPLICATE_TENANT: ["مؤسسة مكررة", "Duplicate tenant"],
  CUSTOMER_UPGRADE: ["ترقية بطلب العميل", "Customer upgrade"],
  CUSTOMER_DOWNGRADE: ["تخفيض بطلب العميل", "Customer downgrade"],
  PLAN_VERSION_MIGRATION: ["ترحيل إلى نسخة باقة أخرى", "Plan version migration"],
  COMMERCIAL_PLAN_CHANGE: ["تغيير باقة تجاري", "Commercial plan change"],
  SUPPORT_CORRECTION: ["تصحيح دعم", "Support correction"]
};

function actionDefinitions(state: PlatformSubscriptionAdministrationState): ActionDefinition[] {
  const status = state.storedStatus;
  if (status === "Trial") {
    return [
      { id: "extend-trial", ar: "تمديد التجربة", en: "Extend trial", level: 1, reasonGroup: "trial", dateKind: "trial" },
      { id: "activate", ar: "تفعيل اشتراك", en: "Activate subscription", level: 2, reasonGroup: "activation", dateKind: "period", needsPlan: true },
      { id: "change-plan", ar: "تغيير باقة التجربة", en: "Change trial plan", level: 2, reasonGroup: "plan", needsPlan: true },
      { id: "end-trial", ar: "إنهاء التجربة الآن", en: "End trial now", level: 3, phrase: "END TRIAL", reasonGroup: "endTrial" }
    ];
  }
  if (status === "Active") {
    return [
      { id: "extend-period", ar: "تمديد الفترة", en: "Extend period", level: 1, reasonGroup: "activation", defaultReason: "MANUAL_RENEWAL", dateKind: "period" },
      { id: "mark-past-due", ar: "وضع متأخر السداد", en: "Mark past due", level: 2, reasonGroup: "collection", defaultReason: "COLLECTION_PENDING", dateKind: "grace" },
      { id: "change-plan", ar: "تغيير الباقة", en: "Change plan", level: 2, reasonGroup: "plan", needsPlan: true },
      { id: "schedule-cancellation", ar: "إلغاء بنهاية الفترة", en: "Cancel at period end", level: 2, reasonGroup: "cancellation" },
      { id: "suspend", ar: "تعليق الاشتراك", en: "Suspend subscription", level: 3, phrase: "SUSPEND", reasonGroup: "suspension" },
      { id: "cancel-now", ar: "إلغاء فوري", en: "Cancel immediately", level: 3, phrase: "CANCEL NOW", reasonGroup: "cancellation" }
    ];
  }
  if (status === "PastDue") {
    return [
      { id: "extend-grace", ar: "تمديد فترة السماح", en: "Extend grace", level: 1, reasonGroup: "collection", dateKind: "grace" },
      { id: "restore-active", ar: "إعادة إلى فعال", en: "Restore active", level: 1, reasonGroup: "collection" },
      { id: "change-plan", ar: "تغيير الباقة", en: "Change plan", level: 2, reasonGroup: "plan", needsPlan: true },
      { id: "suspend", ar: "تعليق الاشتراك", en: "Suspend subscription", level: 3, phrase: "SUSPEND", reasonGroup: "suspension" },
      { id: "cancel-now", ar: "إلغاء فوري", en: "Cancel immediately", level: 3, phrase: "CANCEL NOW", reasonGroup: "cancellation" }
    ];
  }
  if (status === "Suspended") {
    return [
      { id: "reactivate", ar: "إعادة التفعيل", en: "Reactivate", level: 1, reasonGroup: "activation" },
      { id: "change-plan", ar: "تغيير الباقة", en: "Change plan", level: 2, reasonGroup: "plan", needsPlan: true },
      { id: "cancel-now", ar: "إلغاء فوري", en: "Cancel immediately", level: 3, phrase: "CANCEL NOW", reasonGroup: "cancellation" }
    ];
  }
  if (status === "Cancelled") {
    if (state.cancelAtPeriodEnd) {
      return [
        { id: "undo-cancellation", ar: "إلغاء طلب الإلغاء", en: "Undo cancellation", level: 1, reasonGroup: "cancellation" },
        { id: "cancel-now", ar: "الإلغاء فورًا", en: "Cancel now", level: 3, phrase: "CANCEL NOW", reasonGroup: "cancellation" }
      ];
    }
    return [{ id: "activate", ar: "تفعيل فترة جديدة", en: "Activate new period", level: 2, reasonGroup: "activation", dateKind: "period", needsPlan: true }];
  }
  if (status === "Expired") {
    return [{ id: "activate", ar: "تفعيل فترة جديدة", en: "Activate new period", level: 2, reasonGroup: "activation", dateKind: "period", needsPlan: true }];
  }
  return [];
}

function toIso(localValue: string): string {
  const value = new Date(localValue);
  if (Number.isNaN(value.getTime())) throw new Error("INVALID_DATE");
  return value.toISOString();
}

function toLocalDateInput(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function defaultLocalDate(days: number): string {
  return toLocalDateInput(new Date(Date.now() + days * 24 * 60 * 60 * 1000));
}

function defaultLocalDateAfter(boundary: string | null, days: number): string {
  const now = new Date();
  const parsedBoundary = boundary ? new Date(boundary) : null;
  const base = parsedBoundary && !Number.isNaN(parsedBoundary.getTime()) && parsedBoundary.getTime() > now.getTime()
    ? parsedBoundary
    : now;
  return toLocalDateInput(new Date(base.getTime() + days * 24 * 60 * 60 * 1000));
}

function formatDateTime(value: string | null, language: Language): string {
  if (!value) return language === "ar" ? "غير محدد" : "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(language === "ar" ? "ar-AE-u-nu-latn" : "en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZoneName: "short"
  }).format(date);
}

function formatReason(code: string, language: Language): string {
  const label = REASON_LABELS[code];
  return label?.[language === "ar" ? 0 : 1] ?? code;
}

function formatActionImpact(
  action: ActionDefinition | null,
  state: PlatformSubscriptionAdministrationState,
  language: Language
): string | null {
  if (!action) return null;
  const ar = language === "ar";
  if (action.id === "end-trial") {
    return ar
      ? "سيتم إنهاء التجربة فورًا وقد تفقد المؤسسة إمكانية الوصول إلى النظام. هذا الإجراء لا ينتظر تاريخ انتهاء التجربة الحالي."
      : "The trial will end immediately and the tenant may lose access. This does not wait for the current trial end date.";
  }
  if (action.id === "schedule-cancellation") {
    const boundary = formatDateTime(state.currentPeriodEnd, language);
    return ar
      ? `سيتم تسجيل الإلغاء الآن، مع استمرار الوصول حتى نهاية الفترة الحالية: ${boundary}. بعد هذا الموعد ينتهي الاشتراك ما لم يُلغَ طلب الإلغاء قبل ذلك.`
      : `Cancellation will be recorded now, while access continues until the current period ends at ${boundary}. The subscription then expires unless the cancellation is revoked first.`;
  }
  if (action.id === "suspend") {
    return ar
      ? "سيصبح وصول المؤسسة مقيّدًا فورًا. التعليق لا يمدد نهاية الفترة الحالية تلقائيًا، وقد تتطلب إعادة التفعيل بدء فترة جديدة إذا انتهت الفترة أثناء التعليق."
      : "Tenant access becomes restricted immediately. Suspension does not extend the current period automatically, and reactivation may require a new period if the current period expires while suspended.";
  }
  if (action.id === "cancel-now") {
    return ar
      ? "سيتم إنهاء الاشتراك فورًا، وضبط نهاية الفترة الحالية ووقت الانتهاء إلى وقت التنفيذ، وسيصبح وصول المؤسسة مقيّدًا فورًا. هذا ليس طلب إلغاء مؤجلًا."
      : "The subscription ends immediately: the current period end and ended-at time are set to the execution time, and tenant access becomes restricted immediately. This is not a scheduled cancellation.";
  }
  return null;
}

function formatBlocker(code: string, ar: boolean): string {
  const parts = code.split(":");
  if (parts[0] === "TARGET_LIMIT_EXCEEDED" && parts.length === 4) {
    const [, key, usage, limit] = parts;
    return ar
      ? `لا يمكن تغيير الباقة: الاستخدام الحالي لـ ${key} هو ${usage} بينما الباقة المستهدفة تسمح بـ ${limit} فقط.`
      : `Plan change blocked: current ${key} usage is ${usage}, while the target plan allows ${limit}.`;
  }
  if (parts[0] === "TARGET_ENTITLEMENT_DISABLED" && parts.length === 3) {
    const [, key, usage] = parts;
    return ar
      ? `لا يمكن تغيير الباقة: الميزة ${key} مستخدمة حاليًا (${usage}) لكنها غير مفعلة في الباقة المستهدفة.`
      : `Plan change blocked: ${key} is currently in use (${usage}) but is disabled in the target plan.`;
  }
  const known: Record<string, [string, string]> = {
    TARGET_PLAN_VERSION_IS_CURRENT: ["الباقة المستهدفة هي الباقة الحالية نفسها.", "The target plan version is already current."],
    TARGET_PLAN_INACTIVE: ["الباقة المستهدفة غير فعالة.", "The target plan is inactive."],
    TARGET_PLAN_VERSION_NOT_PUBLISHED: ["نسخة الباقة المستهدفة غير منشورة.", "The target plan version is not published."],
    TARGET_PLAN_VERSION_NOT_EFFECTIVE: ["نسخة الباقة المستهدفة ليست ضمن فترة السريان الحالية.", "The target plan version is not currently effective."],
    TARGET_PLAN_ACTIVATION_MODE_INCOMPATIBLE: ["نوع تفعيل الباقة المستهدفة غير متوافق مع حالة الاشتراك الحالية.", "The target plan activation mode is incompatible with the current subscription state."]
  };
  return known[code]?.[ar ? 0 : 1] ?? code;
}

export function PlatformSubscriptionAdminPanel({
  token,
  tenantId,
  language,
  plans,
  onChanged
}: {
  token: string;
  tenantId: string;
  language: Language;
  plans: PlatformPlanSummary[];
  onChanged: () => Promise<void> | void;
}) {
  const ar = language === "ar";
  const [state, setState] = useState<PlatformSubscriptionAdministrationState | null>(null);
  const [planDetails, setPlanDetails] = useState<PlatformPlanDetail[]>([]);
  const [actionId, setActionId] = useState<ActionId | "">("");
  const [reasonCode, setReasonCode] = useState("");
  const [reasonNote, setReasonNote] = useState("");
  const [dateValue, setDateValue] = useState(defaultLocalDate(30));
  const [targetPlanVersionId, setTargetPlanVersionId] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [preview, setPreview] = useState<PlatformPlanChangePreview | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = async () => {
    setBusy(true);
    setError("");
    try {
      const [adminState, ...details] = await Promise.all([
        getPlatformSubscriptionAdministrationState(token, tenantId),
        ...plans.map((plan) => getPlatformPlanDetail(token, plan.planId))
      ]);
      setState(adminState as PlatformSubscriptionAdministrationState);
      setPlanDetails(details as PlatformPlanDetail[]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Subscription administration could not be loaded.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void load(); }, [token, tenantId]);

  const actions = useMemo(() => state ? actionDefinitions(state) : [], [state]);
  const selectedAction = actions.find((action) => action.id === actionId) ?? null;
  const reasonOptions = selectedAction ? REASONS[selectedAction.reasonGroup] : [];
  const eligibleVersions = useMemo(() => {
    const now = Date.now();
    return planDetails.flatMap((plan) => plan.versions
      .filter((version) => plan.isActive
        && version.isPublished
        && version.planVersionId !== state?.planVersionId
        && new Date(version.effectiveFrom).getTime() <= now
        && (!version.effectiveUntil || new Date(version.effectiveUntil).getTime() > now))
      .map((version) => ({ plan, version })));
  }, [planDetails, state?.planVersionId]);

  const targetOptions = useMemo(() => {
    if (!selectedAction?.needsPlan) return [];
    if (selectedAction.id === "activate") return eligibleVersions.filter(({ version }) => version.activationMode !== "Trial");
    if (state?.storedStatus === "Trial") return eligibleVersions.filter(({ version }) => version.activationMode === "Trial");
    return eligibleVersions.filter(({ version }) => version.activationMode !== "Trial");
  }, [eligibleVersions, selectedAction?.id, selectedAction?.needsPlan, state?.storedStatus]);

  const selectedTarget = targetOptions.find((item) => item.version.planVersionId === targetPlanVersionId) ?? null;

  useEffect(() => {
    setReasonCode(selectedAction?.defaultReason ?? "");
    setReasonNote("");
    setConfirmed(false);
    setPhrase("");
    setPreview(null);
    setTargetPlanVersionId("");
    if (selectedAction?.dateKind === "trial") setDateValue(defaultLocalDateAfter(state?.trialEndsAt ?? null, 1));
    if (selectedAction?.dateKind === "period") {
      setDateValue(selectedAction.id === "extend-period"
        ? defaultLocalDateAfter(state?.currentPeriodEnd ?? null, 30)
        : defaultLocalDate(30));
    }
    if (selectedAction?.dateKind === "grace") {
      setDateValue(selectedAction.id === "extend-grace"
        ? defaultLocalDateAfter(state?.graceEndsAt ?? null, 7)
        : defaultLocalDate(7));
    }
  }, [actionId, state?.trialEndsAt, state?.currentPeriodEnd, state?.graceEndsAt]);

  useEffect(() => {
    if (selectedAction?.id !== "change-plan") return;
    if (!selectedTarget) {
      setReasonCode("");
      return;
    }
    const current = planDetails.flatMap((plan) => plan.versions.map((version) => ({ plan, version })))
      .find(({ version }) => version.planVersionId === state?.planVersionId);
    if (!current || current.plan.planId !== selectedTarget.plan.planId) {
      setReasonCode("COMMERCIAL_PLAN_CHANGE");
      return;
    }
    setReasonCode(selectedTarget.version.versionNumber < current.version.versionNumber ? "CUSTOMER_DOWNGRADE" : "CUSTOMER_UPGRADE");
  }, [selectedAction?.id, selectedTarget?.version.planVersionId, state?.planVersionId, planDetails]);

  const impactMessage = state ? formatActionImpact(selectedAction, state, language) : null;
  const canExecute = selectedAction !== null
    && reasonCode.length > 0
    && (!selectedAction.needsPlan || targetPlanVersionId.length > 0)
    && (selectedAction.level < 2 || confirmed)
    && (!selectedAction.phrase || phrase.trim() === selectedAction.phrase)
    && (selectedAction.id !== "change-plan"
      || Boolean(preview && preview.targetPlanVersionId === targetPlanVersionId && preview.canApply));

  const runPreview = async () => {
    if (!targetPlanVersionId) return;
    setBusy(true);
    setError("");
    setPreview(null);
    try {
      setPreview(await previewPlatformPlanChange(token, tenantId, targetPlanVersionId));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Plan preview failed.");
    } finally {
      setBusy(false);
    }
  };

  const execute = async () => {
    if (!state || !selectedAction || !reasonCode) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const common = {
        expectedSubscriptionVersion: state.subscriptionVersion,
        reasonCode,
        reasonNote: reasonNote.trim() || null
      };
      let body: Record<string, unknown> = { ...common };
      if (selectedAction.id === "extend-trial") body = { ...common, newTrialEndsAt: toIso(dateValue) };
      if (selectedAction.id === "activate") body = { ...common, targetPlanVersionId, currentPeriodEnd: toIso(dateValue), confirmed };
      if (selectedAction.id === "extend-period") body = { ...common, newPeriodEnd: toIso(dateValue) };
      if (selectedAction.id === "mark-past-due") body = { ...common, graceEndsAt: toIso(dateValue), confirmed };
      if (selectedAction.id === "extend-grace") body = { ...common, newGraceEndsAt: toIso(dateValue) };
      if (["end-trial", "restore-active", "suspend", "reactivate", "schedule-cancellation", "undo-cancellation", "cancel-now"].includes(selectedAction.id)) {
        body = { ...common, confirmed: selectedAction.level === 1 ? true : confirmed, confirmationPhrase: phrase || null };
      }
      if (selectedAction.id === "change-plan") body = { ...common, targetPlanVersionId, confirmed };

      if (selectedAction.needsPlan && !targetPlanVersionId) throw new Error(ar ? "اختر الباقة المستهدفة." : "Select a target plan version.");
      if (selectedAction.level >= 2 && !confirmed) throw new Error(ar ? "يجب تأكيد مراجعة العملية." : "Review confirmation is required.");
      if (selectedAction.phrase && phrase.trim() !== selectedAction.phrase) throw new Error(ar ? `اكتب ${selectedAction.phrase} للتأكيد.` : `Type ${selectedAction.phrase} to confirm.`);
      if (selectedAction.id === "change-plan" && (!preview || preview.targetPlanVersionId !== targetPlanVersionId || !preview.canApply)) {
        throw new Error(ar ? "نفّذ معاينة تغيير الباقة الناجحة أولًا." : "Run a successful plan-change preview first.");
      }

      await executePlatformSubscriptionCommand(token, tenantId, selectedAction.id, body, createPlatformIdempotencyKey(selectedAction.id));
      setSuccess(ar ? "تم تنفيذ العملية وتسجيلها في سجل المنصة." : "The operation was applied and recorded in platform audit.");
      setActionId("");
      await load();
      await onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Subscription command failed.");
    } finally {
      setBusy(false);
    }
  };

  if (busy && !state) return <section className="platform-admin-panel"><Spinner label={ar ? "تحميل إدارة الاشتراك" : "Loading subscription administration"} /></section>;
  if (!state) return <section className="platform-admin-panel"><div className="platform-admin-error">{error || (ar ? "تعذر تحميل حالة الإدارة." : "Administration state unavailable.")}</div></section>;

  return (
    <section className="platform-admin-panel" data-testid="platform-subscription-admin">
      <div className="platform-admin-panel__heading">
        <div>
          <span className="platform-kicker">SUBSCRIPTION ADMINISTRATION</span>
          <h3>{ar ? "إدارة الاشتراك" : "Subscription administration"}</h3>
          <p>{ar ? "أوامر Control Plane فقط، مع سبب وتدقيق وIdempotency." : "Control-plane commands with reason, audit and idempotency."}</p>
        </div>
        <div className="platform-admin-badges">
          <StatusBadge tone="info">{state.storedStatus}</StatusBadge>
          <StatusBadge tone={state.accessMode === "Full" ? "success" : "warning"}>{state.accessMode}</StatusBadge>
          <small>v{state.subscriptionVersion}</small>
        </div>
      </div>

      {error ? <div className="platform-admin-error" role="alert">{error}</div> : null}
      {success ? <div className="platform-admin-success" role="status">{success}</div> : null}
      {impactMessage ? <div className="platform-admin-error" role="note">{impactMessage}</div> : null}

      <div className="platform-admin-form-grid">
        <SelectField label={ar ? "العملية" : "Action"} value={actionId} onChange={(event) => setActionId(event.target.value as ActionId | "")}>
          <option value="">{ar ? "اختر عملية" : "Select an action"}</option>
          {actions.map((action) => <option key={action.id} value={action.id}>{ar ? action.ar : action.en}</option>)}
        </SelectField>

        {selectedAction ? (
          <SelectField label={ar ? "السبب" : "Reason"} value={reasonCode} onChange={(event) => setReasonCode(event.target.value)}>
            <option value="">{ar ? "اختر السبب" : "Select a reason"}</option>
            {reasonOptions.map((reason) => <option key={reason} value={reason}>{formatReason(reason, language)}</option>)}
          </SelectField>
        ) : null}

        {selectedAction?.dateKind ? (
          <label className="platform-admin-native-field">
            <span>{selectedAction.dateKind === "trial" ? (ar ? "نهاية التجربة الجديدة" : "New trial end") : selectedAction.dateKind === "grace" ? (ar ? "نهاية السماح" : "Grace end") : (ar ? "نهاية الفترة" : "Period end")}</span>
            <input type="datetime-local" value={dateValue} onChange={(event) => setDateValue(event.target.value)} />
            <small>{ar ? `الموعد المحدد: ${formatDateTime(dateValue, language)}` : `Selected time: ${formatDateTime(dateValue, language)}`}</small>
          </label>
        ) : null}

        {selectedAction?.needsPlan ? (
          targetOptions.length ? (
            <SelectField label={ar ? "نسخة الباقة المستهدفة" : "Target plan version"} value={targetPlanVersionId} onChange={(event) => { setTargetPlanVersionId(event.target.value); setPreview(null); }}>
              <option value="">{ar ? "اختر الباقة" : "Select plan"}</option>
              {targetOptions.map(({ plan, version }) => (
                <option key={version.planVersionId} value={version.planVersionId}>{plan.code} · v{version.versionNumber} · {version.activationMode}</option>
              ))}
            </SelectField>
          ) : (
            <div className="platform-admin-error">
              {selectedAction.id === "activate"
                ? (ar ? "لا توجد حاليًا باقة منشورة صالحة للتفعيل. أنشئ أو انشر باقة تجارية أولًا." : "No published plan version is currently eligible for activation. Create or publish a commercial plan first.")
                : (ar ? "لا توجد نسخة باقة أخرى صالحة لهذه العملية." : "No other eligible plan version is available for this operation.")}
            </div>
          )
        ) : null}

        {selectedAction ? <Field label={ar ? "ملاحظة السبب" : "Reason note"} value={reasonNote} onChange={(event) => setReasonNote(event.target.value)} placeholder={ar ? "اختياري · بدون أسرار أو بيانات حساسة" : "Optional · no secrets or sensitive data"} /> : null}

        {selectedAction?.phrase ? <Field label={ar ? `اكتب ${selectedAction.phrase}` : `Type ${selectedAction.phrase}`} value={phrase} onChange={(event) => setPhrase(event.target.value)} /> : null}
      </div>

      {selectedAction?.level && selectedAction.level >= 2 ? (
        <label className="platform-admin-confirm">
          <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
          <span>{ar ? "راجعت الحالة الحالية وتأثير العملية وأؤكد التنفيذ." : "I reviewed the current state and impact and confirm execution."}</span>
        </label>
      ) : null}

      {selectedAction?.id === "change-plan" && targetPlanVersionId ? (
        <div className="platform-admin-preview-actions">
          <Button variant="secondary" type="button" disabled={busy} onClick={() => void runPreview()}>{ar ? "معاينة تغيير الباقة" : "Preview plan change"}</Button>
        </div>
      ) : null}

      {preview ? (
        <div className={`platform-plan-preview ${preview.canApply ? "is-ok" : "is-blocked"}`}>
          <p><strong>{ar ? "الباقة الحالية:" : "Current plan:"}</strong> {preview.currentPlanCode} · v{preview.currentPlanVersionNumber}</p>
          <p><strong>{ar ? "الباقة المستهدفة:" : "Target plan:"}</strong> {preview.targetPlanCode} · v{preview.targetPlanVersionNumber}</p>
          <p>{ar ? `الاستخدام: ${preview.branchCount} فروع · ${preview.userCount} مستخدم · ${preview.deviceCount} جهاز` : `Usage: ${preview.branchCount} branches · ${preview.userCount} users · ${preview.deviceCount} devices`}</p>
          {preview.blockers.length ? <ul>{preview.blockers.map((item) => <li key={item}>{formatBlocker(item, ar)} <small>({item})</small></li>)}</ul> : <p>{ar ? "لا توجد موانع." : "No blockers."}</p>}
          {preview.warnings.length ? <ul>{preview.warnings.map((item) => <li key={item}>{item}</li>)}</ul> : null}
        </div>
      ) : null}

      {selectedAction ? (
        <div className="platform-admin-actions">
          <Button type="button" disabled={busy || !canExecute} onClick={() => void execute()}>{busy ? "…" : ar ? "مراجعة وتنفيذ" : "Review & execute"}</Button>
          <Button variant="secondary" type="button" disabled={busy} onClick={() => setActionId("")}>{ar ? "إلغاء" : "Cancel"}</Button>
        </div>
      ) : null}
    </section>
  );
}
